import React, { useRef, useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { ArrowLeft, Search, Trash2 } from "lucide-react";
import images from "../../assets/images";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useTrainingStore, usePremisesStore } from "../../store/store";
import { Training, Test } from "../../types/api";
import { getS3SignedUrl } from "../../utils/s3Client";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { SpinLoader } from "../../components/loader/Loader";
import { XIcon } from "lucide-react";
import { useTrainingModule } from "./TrainingModuleContext";
import { Dropdown } from "../../components/dropdown/Dropdown";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const UpcomingSessions: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState<Training[]>([]);
  const [orguserRowData, setOrguserRowData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState("");
  const [filePreview, setFilePreview] = useState<{ url: string; name: string }>(
    {
      url: "",
      name: "",
    },
  );

  const { user } = useAuth();
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const {
    trainings,
    fetchTrainings,
    toggleTrainingStatus: toggleTrainingStatusStore,
    deleteTraining,
  } = useTrainingStore();

  const [trainingToDelete, setTrainingToDelete] = useState<{
    id: string;
    topic: string;
  } | null>(null);

  const handleDeleteTraining = (id: string, topic: string) => {
    setTrainingToDelete({ id, topic });
  };

  const confirmDeleteTraining = async () => {
    const target = trainingToDelete;
    if (!target) return;
    setTrainingToDelete(null);
    try {
      await deleteTraining(target.id);
    } catch (err) {
      console.error("Error deleting training:", err);
    }
  };
  const { fetchPremises } = usePremisesStore();
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [filterStartDate, filterEndDate] = dateRange;
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  const userRoles = user?.roles || [];
  const effectiveRole = userRoles.includes("orgadmin")
    ? "orgadmin"
    : userRoles.includes("orguser")
      ? "orguser"
      : "trainee";
  const isTrainer =
    effectiveRole === "orgadmin" ||
    (effectiveRole === "orguser" && viewMode === "trainer");

  // Region/premises/date-range/role scoping is now all done server-side
  // (TrainingController.js's `all` action) instead of fetching everything
  // and re-filtering client-side.
  useEffect(() => {
    const fetchTrainingsData = async () => {
      try {
        setLoading(true);

        await fetchTrainings({
          type: "upcoming",
          where: isFixedCategory
            ? { moduleType, category: validCategories[0] }
            : { moduleType },
          region: selectedRegion || undefined,
          premises: selectedPremises || undefined,
          roleFilter:
            effectiveRole === "orguser"
              ? viewMode === "trainer"
                ? "trainer"
                : "trainee"
              : undefined,
        });
      } catch (err) {
        setError("Error fetching trainings");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrainingsData();
  }, [
    fetchTrainings,
    moduleType,
    isFixedCategory,
    validCategories,
    selectedRegion,
    selectedPremises,
    effectiveRole,
    viewMode,
  ]);

  // `trainings` from the store is already scoped — just split it into the
  // trainer/trainee row shapes this screen renders, using the canonical
  // hasPostTestCompleted/displayedTestCategory fields computed server-side
  // instead of re-deriving them from raw test.status here.
  useEffect(() => {
    const trainerData: Training[] = [];
    const orguserData: any[] = [];

    for (const training of Array.isArray(trainings) ? trainings : []) {
      if (!training.id) continue;
      const displayedTestCategory =
        training.displayedTestCategory || "Pre-Test";
      const tests: Test[] = training.tests || [];
      const displayedTest = tests.find(
        (t) => t.testCategory === displayedTestCategory,
      );
      const scheduledTestDate = displayedTest?.date
        ? formatDate(displayedTest.date)
        : "Not Scheduled";

      if (viewMode === "trainee") {
        const trainee = training.trainees?.find(
          (t) => t.traineeId === user?.id,
        );
        const hasUserSubmittedTest =
          trainee?.results?.[
            displayedTestCategory === "Pre-Test"
              ? "preTestScore"
              : "postTestScore"
          ] !== null &&
          trainee?.results?.[
            displayedTestCategory === "Pre-Test"
              ? "preTestScore"
              : "postTestScore"
          ] !== undefined;
        if (
          trainee &&
          (training.isUpcoming || !training.hasPostTestCompleted) &&
          !hasUserSubmittedTest
        ) {
          orguserData.push({
            ...training,
            scheduledTestDate,
            displayedTestCategory,
          });
        }
      } else {
        // Trainer view (or org admin, who has no viewMode toggle) — the
        // server already scoped `trainings` to this trainer's own
        // instructed sessions (or everything, for org admin).
        if (!training.hasPostTestCompleted) {
          trainerData.push(training);
        }
      }
    }

    setRowData(trainerData);
    setOrguserRowData(orguserData);

    setTimeout(() => {
      gridRef.current?.api?.sizeColumnsToFit();
    }, 0);
  }, [trainings, user?.id, viewMode]);

  useEffect(() => {
    if (effectiveRole !== "orguser") return;
    const saved = localStorage.getItem("training_view_mode");
    if (!saved) {
      localStorage.setItem("training_view_mode", "trainee");
      setViewMode("trainee");
      return;
    }
    if (saved === "trainer" || saved === "trainee") {
      setViewMode(saved);
    }
  }, []);

  const filteredRowData = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return rowData.filter((training) => {
      // Date filter
      if (filterStartDate || filterEndDate) {
        const trainingDate = new Date(training.date);

        if (filterStartDate) {
          const startDate = new Date(filterStartDate);
          startDate.setHours(0, 0, 0, 0);

          if (trainingDate < startDate) {
            return false;
          }
        }

        if (filterEndDate) {
          const endDate = new Date(filterEndDate);
          endDate.setHours(23, 59, 59, 999);

          if (trainingDate > endDate) {
            return false;
          }
        }
      }

      // Search filter
      if (!query) return true;

      return (
        training.topic.toLowerCase().includes(query) ||
        training.category.toLowerCase().includes(query)
      );
    });
  }, [rowData, searchQuery, filterStartDate, filterEndDate]);

  const filteredOrguserRowData = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return orguserRowData.filter((session) => {
      // Date filter
      if (filterStartDate || filterEndDate) {
        const sessionDate = new Date(session.date);

        if (filterStartDate) {
          const startDate = new Date(filterStartDate);
          startDate.setHours(0, 0, 0, 0);

          if (sessionDate < startDate) {
            return false;
          }
        }

        if (filterEndDate) {
          const endDate = new Date(filterEndDate);
          endDate.setHours(23, 59, 59, 999);

          if (sessionDate > endDate) {
            return false;
          }
        }
      }

      // Search filter
      if (!query) return true;

      return (
        session.topic.toLowerCase().includes(query) ||
        session.displayedTestCategory.toLowerCase().includes(query)
      );
    });
  }, [orguserRowData, searchQuery, filterStartDate, filterEndDate]);

  const toggleStatus = async (id: string) => {
    try {
      // Updates the shared training store so every derived view (this
      // screen's filters included) reflects the new status consistently,
      // instead of only patching this screen's local row state.
      await toggleTrainingStatusStore(id);
    } catch (err) {
      setError("Error toggling status");
      console.error("Toggle status error:", err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleViewFile = async (fileUrl: string) => {
    try {
      const key = fileUrl.replace(
        "https://dev-kc-repo.s3.ap-south-1.amazonaws.com/",
        "",
      );

      // Get extension to determine MIME type
      const ext = key.split(".").pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        txt: "text/plain",
        jpg: "image/jpeg",
        png: "image/png",
      };
      const mime = mimeTypes[ext || ""] || "application/octet-stream";

      const signed = await getS3SignedUrl(key, mime);
      setFilePreview({ url: signed, name: key.split("/").pop() || "document" });
    } catch (error) {
      console.error("Error viewing file:", error);
    }
  };
  const handleCloseFile = () => {
    setFilePreview({ url: "", name: "" });
  };

  const trainerColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1 },
    { headerName: "Category", field: "category", flex: 1 },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Total Trainees",
      field: "trainees",
      flex: 1,
      valueGetter: (params) =>
        Array.isArray(params.data.trainees) ? params.data.trainees.length : 0,
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        const isActive = params.value === "Active";
        return (
          <button
            onClick={() => toggleStatus(params.data.id)}
            className={`relative flex items-center w-24 h-8 rounded-full font-medium text-sm 
          transition-colors duration-300 focus:outline-none focus:ring-0 focus:border-none ${
            isActive ? "bg-teal-100 text-teal-800" : "bg-red-100 text-gray-800"
          }`}
          >
            <span className={`absolute z-10 ${isActive ? "left-2" : "left-9"}`}>
              {isActive ? "Active" : "Inactive"}
            </span>
            <span
              className={`absolute w-6 h-6 rounded-full transition-transform duration-300 ${
                isActive
                  ? "bg-teal-500 translate-x-[64px]"
                  : "bg-gray-400 translate-x-1"
              }`}
            />
          </button>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => (
        <div className="flex items-center gap-2">
          <Link to={paths.upcomingSessionDetails(params.data.id)}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </Link>
          <Link to={paths.editTraining(params.data.id)}>
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </Link>
          <button
            type="button"
            onClick={() =>
              handleDeleteTraining(params.data.id, params.data.topic)
            }
            title="Delete"
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  const orguserColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1 },
    { headerName: "Category", field: "displayedTestCategory", flex: 1 },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Material",
      field: "files",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        const files: string[] = params.data.files;

        if (!files || files.length === 0) {
          return <span>No files</span>;
        }

        return (
          <button
            onClick={() => handleViewFile(files[0])}
            className="text-[#0fa9a6] font-semibold underline"
          >
            View
          </button>
        );
      },
      //  : (
      //     <span>No files</span>
      //   ),
    },
    {
      headerName: "Scheduled Test Date",
      field: "scheduledTestDate",
      flex: 1,
    },
  ];

  return (
    <div className="px-2 py-4 mt-14">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Upcoming Sessions</h2>
        <div className="flex items-center gap-4">
          {effectiveRole === "orguser" && (
            <select
              value={viewMode}
              onChange={(e) =>
                setViewMode(e.target.value as "trainee" | "trainer")
              }
              className="border-2 border-[#a5b7cf] p-2 rounded"
            >
              <option value="trainee">Trainee View</option>
              <option value="trainer">Trainer View</option>
            </select>
          )}
          <Link to={paths.dashboard}>
            <button className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white">
              <ArrowLeft className="mr-2 h-4 w-4" /> BACK
            </button>
          </Link>
        </div>
      </div>

      <div className="flex flex-nowrap items-center gap-3 mb-4 overflow-visible relative z-[9999]">
        <div className="relative flex-1 min-w-[180px] max-w-md flex-shrink">
          <Search className="w-5 h-5 text-[#61748e] absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />

          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border-2 border-[#a5b7cf] rounded-lg pl-10 pr-4 py-2 text-[#5a687c] placeholder:text-[#666768] outline-none focus:outline-none"
          />
        </div>

        <div className="flex-shrink-0">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={(value) => {
              setSelectedRegion(value);
              setSelectedPremises("");
            }}
            type="region"
          />
        </div>

        <div className="flex-shrink-0">
          <Dropdown
            selectedValue={selectedPremises}
            onChange={setSelectedPremises}
            type="premises"
            selectedRegion={selectedRegion}
          />
        </div>

        <div className="flex-shrink-0 relative z-[9999]">
          <DateSelector
            mode="upcoming"
            onDateChange={(start, end) => setDateRange([start, end])}
          />
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <SpinLoader />
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            columnDefs={isTrainer ? trainerColumnDefs : orguserColumnDefs}
            rowData={isTrainer ? filteredRowData : filteredOrguserRowData}
            rowHeight={50}
            headerHeight={65}
            paginationPageSize={20}
            pagination={true}
            domLayout="autoHeight"
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            getRowStyle={() => ({
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              marginBottom: "8px",
              borderRadius: "4px",
              backgroundColor: "white",
            })}
          />
        </div>
      )}
      {filePreview.url !== "" && (
        <div
          className="absmiddle"
          style={{
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <XIcon
            className="absolute right-4 top-2 z-10 cursor-pointer bg-red-500 text-white"
            style={{ right: "115px" }}
            onClick={handleCloseFile}
          />
          <div
            className="absmiddle bg-white border py-9"
            style={{
              width: "calc(100vw - 200px)",
              height: "100vh",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <DocumentViewer file={filePreview.url} name={filePreview.name} />
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!trainingToDelete}
        title={`Are you sure you want to delete "${trainingToDelete?.topic}"?`}
        onConfirm={confirmDeleteTraining}
        onCancel={() => setTrainingToDelete(null)}
      />
    </div>
  );
};

export default UpcomingSessions;
